import { useState } from 'react';
import { Card, Form, Input, Space, Button, Typography, App } from 'antd';
import { useLanguages } from '@/hooks/useLanguages';
import { useUpsertTermTranslation } from '@/hooks/useGlossary';
import type { GlossaryTerm } from '@/types/glossary';

const { Text } = Typography;

interface Props {
  term: GlossaryTerm;
}

export function ExpandedTermRow({ term }: Props) {
  const { message } = App.useApp();
  const langsQ = useLanguages();
  const upsert = useUpsertTermTranslation();
  const [drafts, setDrafts] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    for (const t of term.translations) m[t.lang_code] = t.translation;
    return m;
  });

  if (term.preserve_as_is) {
    return (
      <Card size="small">
        <Text type="secondary">
          &quot;{term.term}&quot; is marked <Text code>preserve_as_is</Text> — per-language
          translations are not used.
        </Text>
      </Card>
    );
  }

  async function save(lang: string) {
    const v = drafts[lang] ?? '';
    if (v.trim() === '') return;
    try {
      await upsert.mutateAsync({ termId: term.id, lang, translation: v });
      message.success(`Saved ${lang}`);
    } catch (e) {
      message.error((e as Error).message);
    }
  }

  const nonBundled = (langsQ.data ?? []).filter((l) => !l.is_bundled || l.code === 'zh-Hans');

  return (
    <Card size="small" title="Per-language translations">
      <Form layout="vertical">
        {nonBundled.map((l) => (
          <Form.Item
            key={l.code}
            label={`${l.code} — ${l.name_native}`}
            style={{ marginBottom: 8 }}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={drafts[l.code] ?? ''}
                onChange={(e) => setDrafts({ ...drafts, [l.code]: e.target.value })}
                placeholder={`Translation of "${term.term}" in ${l.code}`}
                onBlur={() => save(l.code)}
              />
              <Button onClick={() => save(l.code)} loading={upsert.isPending}>
                Save
              </Button>
            </Space.Compact>
          </Form.Item>
        ))}
      </Form>
    </Card>
  );
}
